import React from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CartProvider } from './contexts/CartContext';
import Home from './pages/home';
import ContactPage from './pages/innerpages/contact';
import PortfolioPage from './pages/innerpages/portfolio';
import AboutPage from './pages/innerpages/about';
import ProductsPage from './pages/innerpages/products';
import ProductDetailPage from './pages/innerpages/product-detail';
import CheckoutPage from './pages/innerpages/checkout';
import OrderConfirmationPage from './pages/innerpages/order-confirmation';
import CheckoutExpiredPage from './pages/innerpages/checkout-expired';
import FindOrderPage from './pages/innerpages/find-order';
import OrderStatusPage from './pages/innerpages/order-status';

// Create a QueryClient instance with 24-hour cache
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
      gcTime: 24 * 60 * 60 * 1000, // 24 hours (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1, // Only retry failed requests once
    },
  },
});

// Create a persister that uses localStorage to persist the cache across page refreshes
const persister = {
  persistClient: async (client: any) => {
    try {
      const data = JSON.stringify(client);
      window.localStorage.setItem('REACT_QUERY_OFFLINE_CACHE', data);
    } catch (error) {
      console.error('Failed to persist query client:', error);
    }
  },
  restoreClient: async () => {
    try {
      const data = window.localStorage.getItem('REACT_QUERY_OFFLINE_CACHE');
      return data ? JSON.parse(data) : undefined;
    } catch (error) {
      console.error('Failed to restore query client:', error);
      return undefined;
    }
  },
  removeClient: async () => {
    try {
      window.localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
    } catch (error) {
      console.error('Failed to remove query client:', error);
    }
  },
};

const App: React.FC = () => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <CartProvider>
        <Router>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout-expired" element={<CheckoutExpiredPage />} />
            <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
            <Route path="/find-order" element={<FindOrderPage />} />
            <Route path="/order-status" element={<OrderStatusPage />} />
            <Route path="/order-status/:orderId" element={<OrderStatusPage />} />
          </Routes>
        </Router>
      </CartProvider>
      {/* React Query Devtools - only shows in development */}
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  );
}

export default App;
